import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, query as firestoreQuery, onSnapshot } from 'firebase/firestore';

/*
  To run this React application locally, you need to have the Firebase JavaScript SDK installed
  in your project. If you encounter a "Cannot find module" or "Can't resolve" error
  for 'firebase/app', 'firebase/auth', or 'firebase/firestore', please run one of
  the following commands in your project's terminal:

  Using npm:
  npm install firebase

  Using yarn:
  yarn add firebase

  For local development, you also need to manually provide your Firebase configuration.
  Replace the placeholder values in the `localFirebaseConfig` object below with your
  actual project credentials.
*/

// === START: LOCAL CONFIGURATION ===
// --------------------------------------------------------------------------
// IMPORTANT: Please replace all placeholder values with your actual Firebase config.
// You can find this by going to your project's settings in the Firebase console:
// https://console.firebase.google.com/
// --------------------------------------------------------------------------
const __firebase_config = "";
const __initial_auth_token = null;
const __app_id = "";

const localFirebaseConfig = {
    apiKey: "AIzaSyDNRjNGb22Rq4sk7KMfinnXLrI5-h9DVnQ", // <<< UPDATED
    authDomain: "ai-stchatagent.firebaseapp.com", // <<< UPDATED
    projectId: "ai-stchatagent", // <<< UPDATED
    storageBucket: "ai-stchatagent.firebasestorage.app", // <<< UPDATED
    messagingSenderId: "290265329375", // <<< UPDATED
    appId: "1:290265329375:web:9a06b01e1808d0dd79836a", // <<< UPDATED
    measurementId: "G-91LN20GPWF" // <<< UPDATED
};

// This ensures the local config is used when running outside the hosted environment.
const firebaseConfig = typeof __firebase_config !== 'undefined' && Object.keys(JSON.parse(__firebase_config)).length > 0
    ? JSON.parse(__firebase_config)
    : localFirebaseConfig;

const localAppId = localFirebaseConfig.appId;
const initialAuthToken = typeof __initial_auth_token !== 'undefined' ? __initial_auth_token : null;
const appId = typeof __app_id !== 'undefined' ? __app_id : localAppId;

// === END: LOCAL CONFIGURATION ===

// Log the Firebase configuration object before initialization
console.log("Firebase Configuration used for initialization:", firebaseConfig);

// Initialize Firebase App
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);


// Main App component
const App = () => {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false); // New loading state for summarization
  const [userId, setUserId] = useState(null);

  useEffect(() => {

    if (initialAuthToken) {
      signInWithCustomToken(auth, initialAuthToken).catch(error => {
        console.error("Error signing in with custom token:", error);
        signInAnonymously(auth);
      });
    } else {
      signInAnonymously(auth);
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    if (!db || !userId) {
      return;
    }

    const messagesCollectionRef = collection(db, `artifacts/${appId}/users/${userId}/messages`);
    const q = firestoreQuery(messagesCollectionRef);

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      fetchedMessages.sort((a, b) => (a.timestamp?.toDate() || 0) - (b.timestamp?.toDate() || 0));
      setMessages(fetchedMessages);
    }, (error) => {
      console.error("Error fetching messages:", error);
    });

    return () => unsubscribe();
  }, [db, userId]);

  const handleSummarizeChat = async () => {
    if (isSummarizing) return;
    setIsSummarizing(true);

    try {
      const summaryApiUrl = 'YOUR_GEMINI_SUMMARY_API_GATEWAY_URL/summarize'; // <<< UPDATE THIS

      const payload = {
        chatHistory: messages.map(msg => ({
          role: msg.sender,
          text: msg.text
        }))
      };

      const response = await fetch(summaryApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Summary backend error: ${response.status} ${response.statusText}`);
      }

      const summaryData = await response.json();
      const summaryText = typeof summaryData.summary === 'string' ? summaryData.summary : 'Could not generate a summary.';

      const summaryMessage = {
        text: `✨ **Summary of your chat:**\n\n${summaryText}`,
        sender: 'bot',
        timestamp: new Date(),
      };

      const messagesCollectionRef = collection(db, `artifacts/${appId}/users/${userId}/messages`);
      await addDoc(messagesCollectionRef, summaryMessage);

    } catch (error) {
      console.error("Error summarizing chat:", error);
      const errorMessage = {
        text: `Error summarizing chat: ${error.message}`,
        sender: 'bot',
        timestamp: new Date(),
        isError: true,
      };
      const messagesCollectionRef = collection(db, `artifacts/${appId}/users/${userId}/messages`);
      await addDoc(messagesCollectionRef, errorMessage);
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleQuerySubmit = async (e) => {
    e.preventDefault();
    const currentQuery = query.trim();
    if (!currentQuery || !db || !userId) return;

    const userMessage = {
      text: currentQuery,
      sender: 'user',
      timestamp: new Date(),
    };

    try {
      const messagesCollectionRef = collection(db, `artifacts/${appId}/users/${userId}/messages`);
      await addDoc(messagesCollectionRef, userMessage);
    } catch (error) {
      console.error("Error adding user message to Firestore:", error);
    }

    setIsLoading(true);
    setQuery('');

    try {
      const apiGatewayUrl = 'https://304zt5iho5.execute-api.us-east-1.amazonaws.com/Dev/agent';

      const innerPayloadObject = {
        query: currentQuery,
      };

      const innerPayloadString = JSON.stringify(innerPayloadObject);

      const payloadToSend = {
        body: innerPayloadString
      };

      const response = await fetch(apiGatewayUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payloadToSend)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Backend error: ${response.status} ${response.statusText}`);
      }

      const apiGatewayResponse = await response.json();

      if (!apiGatewayResponse.body || typeof apiGatewayResponse.body !== 'string') {
        throw new Error("Invalid response format from API Gateway: 'body' field is missing or not a string.");
      }

      const data = JSON.parse(apiGatewayResponse.body);

      const botResponseText = typeof data.response === 'string' ? data.response : 'No valid response text received from agent.';

      const botMessage = {
        text: botResponseText,
        sender: 'bot',
        timestamp: new Date(),
      };

      try {
        const messagesCollectionRef = collection(db, `artifacts/${appId}/users/${userId}/messages`);
        await addDoc(messagesCollectionRef, botMessage);
      } catch (error) {
        console.error("Error adding bot message to Firestore:", error);
      }

    } catch (error) {
      console.error("Error sending query to backend:", error);
      const errorMessage = {
        text: `Error: ${error.message}. Please check your backend setup and API Gateway URL.`,
        sender: 'bot',
        timestamp: new Date(),
        isError: true,
      };
      try {
        const messagesCollectionRef = collection(db, `artifacts/${appId}/users/${userId}/messages`);
        await addDoc(messagesCollectionRef, errorMessage);
      } catch (firestoreError) {
        console.error("Error adding error message to Firestore:", firestoreError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4 font-sans">
      <div className="bg-white shadow-lg rounded-xl p-6 w-full max-w-2xl flex flex-col">
        <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">
          Bedrock Agent Chat
        </h1>

        {userId && (
          <div className="text-sm text-gray-600 mb-4 text-center">
            Your User ID: <span className="font-mono bg-gray-200 px-2 py-1 rounded">{userId}</span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto h-96 border border-gray-300 rounded-lg p-4 mb-4 bg-gray-50 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 mt-10">
              Type a query to start the conversation with your Bedrock Agent!
            </div>
          ) : (
            messages.map((msg, index) => (
              <div
                key={index}
                className={`flex mb-4 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[70%] px-4 py-2 rounded-lg shadow-md ${
                    msg.sender === 'user'
                      ? 'bg-blue-500 text-white rounded-br-none'
                      : msg.isError
                        ? 'bg-red-200 text-red-800 rounded-bl-none'
                        : 'bg-gray-200 text-gray-800 rounded-bl-none'
                  }`}
                >
                  <p className="text-sm">{msg.text}</p>
                  <span className="text-xs opacity-75 mt-1 block">
                    {msg.timestamp && typeof msg.timestamp.toDate === 'function' ? msg.timestamp.toDate().toLocaleTimeString() : 'Sending...'}
                  </span>
                </div>
              </div>
            ))
          )}
          {isLoading && (
            <div className="flex justify-start mb-4">
              <div className="max-w-[70%] px-4 py-2 rounded-lg shadow-md bg-gray-200 text-gray-800 rounded-bl-none">
                <div className="flex items-center">
                  <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-900 mr-2"></span>
                  <span className="text-sm">Agent is thinking...</span>
                </div>
              </div>
            </div>
          )}
          {isSummarizing && (
            <div className="flex justify-start mb-4">
              <div className="max-w-[70%] px-4 py-2 rounded-lg shadow-md bg-green-200 text-green-800 rounded-bl-none">
                <div className="flex items-center">
                  <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-900 mr-2"></span>
                  <span className="text-sm">Generating summary with Gemini...</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleQuerySubmit} className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask your Bedrock Agent a question..."
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
            disabled={isLoading || isSummarizing}
          />
          <button
            type="submit"
            className="bg-blue-600 text-white px-6 py-3 rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading || !db || !userId || isSummarizing}
          >
            {isLoading ? 'Sending...' : 'Send'}
          </button>
        </form>
        <button
          onClick={handleSummarizeChat}
          className="mt-4 w-full bg-green-600 text-white px-6 py-3 rounded-lg shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isSummarizing || isLoading || messages.length < 2 || !db || !userId}
        >
          {isSummarizing ? 'Summarizing...' : '✨ Summarize Chat'}
        </button>
      </div>
    </div>
  );
};

export default App;
